'use client'

import Image from 'next/image'
import Link from 'next/link'
import React, { useRef, useState } from 'react'

import characterBow from '../assets/character_bow.png'
import characterFisherman from '../assets/character_fisherman.png'
import characterHunter from '../assets/character_hunter.png'
import heroBackground from '../assets/hero_bg.jpg'
import { IgamingHobbiesProps } from '../types'
import styles from './IgamingHobbies.module.scss'

type Locale = 'en' | 'es'

type SurveyQuestionType =
  | 'single'
  | 'single-with-other'
  | 'multi-with-other'
  | 'multi-with-other-limit-3'
  | 'text'

type LocalizedText = Record<Locale, string>

type SurveyQuestion = {
  id: number
  question: LocalizedText
  type: SurveyQuestionType
  options?: Record<Locale, string[]>
  placeholder?: LocalizedText
}

type SurveyAnswer = string | string[]

type ConfettiOptions = {
  particleCount: number
  angle: number
  spread: number
  origin: {
    x: number
    y: number
  }
  colors: string[]
}

type ConfettiFunction = (options: ConfettiOptions) => void

type WindowWithConfetti = Window & {
  confetti?: ConfettiFunction
}

const localesDict = {
  en: {
    collaboration: 'COLLABORATION PROJECT',
    mainHobbiesTitle: 'What are the most popular hobbies among iGaming professionals?',
    manifestoTag: 'RESEARCH MANIFESTO',
    manifestoTitle: 'Why are we doing this research?',
    manifestoP1:
      'The iGaming industry is a diverse community of developers, marketers, game designers, analysts, affiliates and many other professionals who bring ideas, products and experiences to life.',
    manifestoP2:
      'Behind every successful game feature, mathematical model, marketing campaign and business decision, there are people with their own stories, passions and interests.',
    manifestoP3:
      'That is why Belatra Games and G&M News decided to show iGaming from a new perspective: through hobbies and passions outside of work. Because behind every product are real people: creative, curious and multifaceted.',
    whyHobby: 'Why a hobby?',
    hobbiesP1:
      'Hobbies reveal a different side of who we are, reflecting our personality, curiosity, passion and the ability to recharge and discover something new.',
    hobbiesP2:
      "This philosophy is reflected in The Guy, the character from Belatra Games' Blast the Bass, Ice Bass and Hunt the Bucks slots. He is always exploring new experiences, from fishing to hunting, and represents people who are not afraid to experiment, both in work and in life.",
    shiningSlogan:
      'Your answers will help us discover the hobbies, passions and hidden talents of the iGaming community!',
    surveyMeta: 'This short survey has 10 questions and takes just 2–3 minutes to complete.',
    beginSurvey: 'Begin Survey',
    charactersSectionHeader:
      "THE GUY'S HOBBIES IN BLAST THE BASS, ICE BASS, AND HUNT THE BUCKS SLOTS",
    charFishermanName: 'Ice Bass',
    charFishermanTitle: 'Winter Fishing',
    charFishermanDesc:
      "The air is frosty, the stakes are high, and the potential for a big catch is greater than ever. It's time to drill a well and see if you can outsmart the giants hiding in the depths.",
    charSummerName: 'Blast the Bass',
    charSummerTitle: 'Summer Fishing',
    charSummerDesc:
      "Deep beneath the ocean's surface lies a world full of exotic fish and treasures. Just you, the boat, and your big catch.",
    charHunterName: 'Hunt the Bucks',
    charHunterTitle: 'Hunting',
    charHunterDesc:
      'After changing the quiet riverbank to a wild one, the hero sets off to make tactical decisions in a dense forest. Will you help him get his prey?',
    charSecretName: 'New slot with The Guy',
    charSecretTitle: '',
    charSecretDesc: 'In secret!',
    charSecretDescSecond:
      'Belatra Games is preparing a new release. At the end of August, The Guy will have a new hobby!',
    swipeScrollHelp: '← Swipe / Scroll to view all →',
    questionMeta: 'QUESTION {current} OF {total}',
    percentComplete: '{percent}% COMPLETE',
    searchPlaceholder: 'Search hobbies...',
    scrollDownHelp: '↓ Scroll down to view all options ↓',
    backBtn: 'Back',
    nextBtn: 'Next Question',
    submitBtn: 'Submit Survey',
    successAlertTitle: 'Thank you for participating!',
    successAlertDesc:
      "Very soon, we'll discover how iGaming professionals live beyond work — which hobbies are becoming new trends, and which remain niche and unexpected passions.",
    publishingInfo:
      'The results of this study will be published on the {gm} website and {belatra} platforms.',
    publishingTextBefore: 'The results of this study will be published on the',
    publishingTextMiddle: 'website and',
    publishingTextAfter: 'platforms.',
    stayTuned: 'Stay tuned for updates!',
    resetBtn: 'Reset Survey Form',
    otherLabel: 'OTHER (PLEASE SPECIFY):',
    otherPlaceholder: 'Type your custom answer...',
    copyright: 'All rights reserved.',
  },
  es: {
    collaboration: 'PROYECTO DE COLABORACIÓN',
    mainHobbiesTitle:
      '¿Cuáles son los pasatiempos más populares entre los profesionales de iGaming?',
    manifestoTag: 'MANIFIESTO DE INVESTIGACIÓN',
    manifestoTitle: '¿Por qué hacemos esta investigación?',
    manifestoP1:
      'La industria del iGaming es una comunidad diversa de desarrolladores, especialistas en marketing, diseñadores de juegos, analistas, afiliados y muchos otros profesionales que dan vida a ideas, productos y experiencias.',
    manifestoP2:
      'Detrás de cada función de juego exitosa, modelo matemático, campaña de marketing y decisión de negocio, hay personas con sus propias historias, pasiones e intereses.',
    manifestoP3:
      'Es por eso que Belatra Games y G&M News decidieron mostrar el iGaming desde una nueva perspectiva: a través de pasatiempos y pasiones fuera del trabajo. Porque detrás de cada producto hay personas reales: creativas, curiosas y polifacéticas.',
    whyHobby: '¿Por qué un pasatiempo?',
    hobbiesP1:
      'Los pasatiempos revelan un lado diferente de quiénes somos: nuestra personalidad, curiosidad, pasión y la capacidad de recargarnos y descubrir algo nuevo.',
    hobbiesP2:
      'Esta filosofía se refleja en The Guy, el personaje de las tragamonedas Blast the Bass, Ice Bass y Hunt the Bucks de Belatra Games. Siempre está explorando nuevas experiencias, desde la pesca hasta la caza, y representa a personas que no temen experimentar, tanto en el trabajo como en la vida.',
    shiningSlogan:
      '¡Tus respuestas nos ayudarán a descubrir los pasatiempos, las pasiones y los talentos ocultos de la comunidad de iGaming!',
    surveyMeta:
      'Esta pequeña encuesta tiene 10 preguntas y toma solamente 2 o 3 minutos completarla',
    beginSurvey: 'Iniciar Encuesta',
    charactersSectionHeader:
      'PASATIEMPOS DE THE GUY EN LAS TRAGAMONEDAS BLAST THE BASS, ICE BASS Y HUNT THE BUCKS',
    charFishermanName: 'Ice Bass',
    charFishermanTitle: 'Pesca de Invierno',
    charFishermanDesc:
      'El aire es helado, las apuestas son altas y el potencial de una gran captura es mayor que nunca. Es hora de perforar un pozo y ver si puedes burlar a los gigantes que se esconden en las profundidades.',
    charSummerName: 'Blast the Bass',
    charSummerTitle: 'Pesca de Verano',
    charSummerDesc:
      'En lo profundo de la superficie del océano se encuentra un mundo lleno de peces exóticos y tesoros. Solo tú, el bote y tu gran captura.',
    charHunterName: 'Hunt the Bucks',
    charHunterTitle: 'Caza',
    charHunterDesc:
      'Después de cambiar la tranquila orilla del río por una salvaje, el héroe se pone en marcha para tomar decisiones tácticas en un denso bosque. ¿Le ayudarás a conseguir su presa?',
    charSecretName: 'Nuevo slot con The Guy',
    charSecretTitle: 'NUEVO PASATIEMPO',
    charSecretDesc: '¡En secreto!',
    charSecretDescSecond:
      'Belatra Games está preparando un nuevo juego. ¡A finales de agosto, The Guy tendrá un nuevo hobby!',
    swipeScrollHelp: '← Desliza / Desplaza para ver todo →',
    questionMeta: 'PREGUNTA {current} DE {total}',
    percentComplete: '{percent}% COMPLETADO',
    searchPlaceholder: 'Buscar pasatiempos...',
    scrollDownHelp: '↓ Desliza hacia abajo para ver todas las opciones ↓',
    backBtn: 'Atrás',
    nextBtn: 'Siguiente Pregunta',
    submitBtn: 'Enviar Encuesta',
    successAlertTitle: '¡Gracias por participar!',
    successAlertDesc:
      'Muy pronto descubriremos cómo viven los profesionales de iGaming más allá del trabajo: qué pasatiempos se están convirtiendo en nuevas tendencias y cuáles siguen siendo pasiones de nicho e inesperadas.',
    publishingInfo:
      'Los resultados de este estudio se publicarán en el sitio web de {gm} y en las plataformas de {belatra}.',
    publishingTextBefore: 'Los resultados de este estudio se publicarán en el sitio web de',
    publishingTextMiddle: 'y en las plataformas de',
    publishingTextAfter: '.',
    stayTuned: '¡Muy pronto, más novedades!',
    resetBtn: 'Reiniciar Formulario',
    otherLabel: 'OTRO (POR FAVOR ESPECIFIQUE):',
    otherPlaceholder: 'Escribe tu respuesta personalizada...',
    copyright: 'Todos los derechos reservados.',
  },
}

const surveyQuestions: SurveyQuestion[] = [
  {
    id: 1,
    question: {
      en: 'Which region are you based in?',
      es: '¿En qué región te encuentras?',
    },
    type: 'single',
    options: {
      en: ['Europe', 'Asia', 'Latin America', 'Africa'],
      es: ['Europa', 'Asia', 'América Latina', 'África'],
    },
  },
  {
    id: 2,
    question: {
      en: 'What is your gender?',
      es: '¿Cuál es tu género?',
    },
    type: 'single',
    options: {
      en: ['Male', 'Female', 'Prefer not to say'],
      es: ['Masculino', 'Femenino', 'Prefiero no decirlo'],
    },
  },
  {
    id: 3,
    question: {
      en: 'What is your role in the iGaming industry?',
      es: '¿Cuál es tu rol en la industria del iGaming?',
    },
    type: 'single-with-other',
    options: {
      en: [
        'C-level / Executive',
        'Design',
        'Marketing',
        'PR & Communications',
        'Game Designer',
        'Developer',
        'QA / Testing',
        'Data Analyst',
        'HR / Talent Acquisition',
        'Affiliate Manager',
        'CRM / Retention',
        'Product Analyst',
        'Compliance',
        'Customer Support',
        'Sales / Business Development',
      ],
      es: [
        'C-level / Ejecutivo',
        'Diseño',
        'Marketing',
        'RRPP & Comunicaciones',
        'Diseñador de Juegos',
        'Desarrollador',
        'QA / Pruebas',
        'Analista de Datos',
        'RRHH / Adquisición de Talento',
        'Gerente de Afiliados',
        'CRM / Retención',
        'Analista de Producto',
        'Cumplimiento',
        'Atención al Cliente',
        'Ventas / Desarrollo de Negocios',
      ],
    },
  },
  {
    id: 4,
    question: {
      en: 'What are your current hobbies?',
      es: '¿Cuáles son tus pasatiempos actuales?',
    },
    type: 'multi-with-other',
    options: {
      en: [
        '3D Printing',
        'Astrology',
        'Basketball',
        'Blogging',
        'Board Games',
        'Boxing',
        'Camping',
        'Card Games',
        'Chess',
        'Coffee Tasting / Specialty Coffee',
        'Collecting',
        'Content Creation',
        'Cooking',
        'Cycling',
        'Dancing',
        'Diving / Scuba Diving',
        'DJing',
        'Dog Training / Cynology',
        'Drawing / Painting',
        'Embroidery',
        'Fishing',
        'Football / Soccer',
        'Gardening',
        'Golf',
        'Hiking',
        'Horse Riding',
        'Hunting',
        'Jigsaw Puzzles',
        'Knitting',
        'Motorcycling',
        'Mountaineering',
        'Padel',
        'Photography',
        'Pilates',
        'Playing a Musical Instrument',
        'Podcasting',
        'Quizzes / Trivia',
        'Reading',
        'Retro Gaming',
        'Rock Climbing',
        'Running',
        'Sculpture / Pottery',
        'Singing',
        'Skiing',
        'Skydiving',
        'Snowboarding',
        'Squash',
        'Stand-up Paddleboarding (SUP)',
        'Streaming',
        'Strength Training / Gym',
        'Surfing',
        'Swimming',
        'Tennis',
        'Video Games',
        'Videography',
        'Volleyball',
        'Volunteering',
        'Wakeboarding',
        'Writing',
        'Yoga',
      ],
      es: [
        'Impresión 3D',
        'Astrología',
        'Baloncesto',
        'Blogging',
        'Juegos de Mesa',
        'Boxeo',
        'Campamento',
        'Juegos de Cartas',
        'Ajedrez',
        'Cata de Café / Café de Especialidad',
        'Coleccionismo',
        'Creación de Contenido',
        'Cocina',
        'Ciclismo',
        'Baile',
        'Buceo',
        'DJing',
        'Entrenamiento de Perros / Cinología',
        'Dibujo / Pintura',
        'Bordado',
        'Pesca',
        'Fútbol',
        'Jardinería',
        'Golf',
        'Senderismo',
        'Equitación',
        'Caza',
        'Rompecabezas',
        'Tejido',
        'Motociclismo',
        'Montañismo',
        'Pádel',
        'Fotografía',
        'Pilates',
        'Tocar un Instrumento Musical',
        'Podcasting',
        'Quizzes / Trivia',
        'Lectura',
        'Gaming Retro',
        'Escalada',
        'Running',
        'Escultura / Alfarería',
        'Canto',
        'Esquí',
        'Paracaidismo',
        'Snowboard',
        'Squash',
        'Paddle Surf (SUP)',
        'Streaming',
        'Gimnasio / Entrenamiento de Fuerza',
        'Surf',
        'Natación',
        'Tenis',
        'Videojuegos',
        'Videografía',
        'Vóleibol',
        'Voluntariado',
        'Wakeboard',
        'Escritura',
        'Yoga',
      ],
    },
  },
  {
    id: 5,
    question: {
      en: 'Why did you choose this hobby?',
      es: '¿Por qué elegiste este pasatiempo?',
    },
    type: 'multi-with-other-limit-3',
    options: {
      en: [
        'It helps me relieve stress and recharge',
        'It keeps me physically active',
        'It allows me to express myself creatively',
        'It gives me time for myself',
        'It helps me learn new skills',
        'I enjoy trying new things',
        "It's a way to spend time with friends or family",
        'It helps me meet new people',
        'I enjoy the challenge and setting goals',
        'It inspires me',
        'It helps me disconnect from work',
        'I simply enjoy it',
      ],
      es: [
        'Me ayuda a aliviar el estrés y recargar energías',
        'Me mantiene físicamente activo',
        'Me permite expresarme creativamente',
        'Me da tiempo para mí mismo',
        'Me ayuda a aprender nuevas habilidades',
        'Disfruto probando cosas nuevas',
        'Es una forma de pasar tiempo con amigos o familiares',
        'Me ayuda a conocer gente nueva',
        'Disfruto del desafío y de fijar metas',
        'Me inspira',
        'Me ayuda a desconectarme del trabajo',
        'Simplemente lo disfruto',
      ],
    },
  },
  {
    id: 6,
    question: {
      en: 'What hobby would you like to pursue in the near future?',
      es: '¿Qué pasatiempo te gustaría probar en el futuro cercano?',
    },
    type: 'multi-with-other',
    options: {
      en: [
        '3D Printing',
        'Astrology',
        'Basketball',
        'Blogging',
        'Board Games',
        'Boxing',
        'Camping',
        'Card Games',
        'Chess',
        'Coffee Tasting / Specialty Coffee',
        'Collecting',
        'Content Creation',
        'Cooking',
        'Cycling',
        'Dancing',
        'Diving / Scuba Diving',
        'DJing',
        'Dog Training / Cynology',
        'Drawing / Painting',
        'Embroidery',
        'Fishing',
        'Football / Soccer',
        'Gardening',
        'Golf',
        'Hiking',
        'Horse Riding',
        'Hunting',
        'Jigsaw Puzzles',
        'Knitting',
        'Motorcycling',
        'Mountaineering',
        'Padel',
        'Photography',
        'Pilates',
        'Playing a Musical Instrument',
        'Podcasting',
        'Quizzes / Trivia',
        'Reading',
        'Retro Gaming',
        'Rock Climbing',
        'Running',
        'Sculpture / Pottery',
        'Singing',
        'Skiing',
        'Skydiving',
        'Snowboarding',
        'Squash',
        'Stand-up Paddleboarding (SUP)',
        'Streaming',
        'Strength Training / Gym',
        'Surfing',
        'Swimming',
        'Tennis',
        'Video Games',
        'Videography',
        'Volleyball',
        'Volunteering',
        'Wakeboarding',
        'Writing',
        'Yoga',
      ],
      es: [
        'Impresión 3D',
        'Astrología',
        'Baloncesto',
        'Blogging',
        'Juegos de Mesa',
        'Boxeo',
        'Campamento',
        'Juegos de Cartas',
        'Ajedrez',
        'Cata de Café / Café de Especialidad',
        'Coleccionismo',
        'Creación de Contenido',
        'Cocina',
        'Ciclismo',
        'Baile',
        'Buceo',
        'DJing',
        'Entrenamiento de Perros / Cinología',
        'Dibujo / Pintura',
        'Bordado',
        'Pesca',
        'Fútbol',
        'Jardinería',
        'Golf',
        'Senderismo',
        'Equitación',
        'Caza',
        'Rompecabezas',
        'Tejido',
        'Motociclismo',
        'Montañismo',
        'Pádel',
        'Fotografía',
        'Pilates',
        'Tocar un Instrumento Musical',
        'Podcasting',
        'Quizzes / Trivia',
        'Lectura',
        'Gaming Retro',
        'Escalada',
        'Running',
        'Escultura / Alfarería',
        'Canto',
        'Esquí',
        'Paracaidismo',
        'Snowboard',
        'Squash',
        'Paddle Surf (SUP)',
        'Streaming',
        'Gimnasio / Entrenamiento de Fuerza',
        'Surf',
        'Natación',
        'Tenis',
        'Videojuegos',
        'Videografía',
        'Vóleibol',
        'Voluntariado',
        'Wakeboard',
        'Escritura',
        'Yoga',
      ],
    },
  },
  {
    id: 7,
    question: {
      en: 'Do your hobbies help reduce work-related stress?',
      es: '¿Tus pasatiempos te ayudan a reducir el estrés del trabajo?',
    },
    type: 'single',
    options: {
      en: ['Yes', 'No', 'Not sure'],
      es: ['Sí', 'No', 'No estoy seguro'],
    },
  },
  {
    id: 8,
    question: {
      en: 'Where do you usually discover new hobbies?',
      es: '¿Dónde sueles descubrir nuevos pasatiempos?',
    },
    type: 'single-with-other',
    options: {
      en: ['Social media', 'Family', 'Friends', 'Colleagues', 'By chance'],
      es: ['Redes sociales', 'Familia', 'Amigos', 'Colegas', 'Por casualidad'],
    },
  },
  {
    id: 9,
    question: {
      en: 'Do you have an unusual or niche hobby?',
      es: '¿Tienes algún pasatiempo inusual o de nicho?',
    },
    type: 'text',
    placeholder: {
      en: 'Tell us about your unique passion...',
      es: 'Cuéntanos sobre tu pasión única...',
    },
  },
  {
    id: 10,
    question: {
      en: "What's the most unexpected hobby you've ever tried?",
      es: '¿Cuál es el pasatiempo más inesperado que has probado alguna vez?',
    },
    type: 'text',
    placeholder: {
      en: 'Share an unexpected activity you tried once...',
      es: 'Comparte esa actividad inesperada que probaste alguna vez...',
    },
  },
]

export function IgamingHobbies({
  locale = 'en',
  landingId,
  landingSlug,
  surveyEndpoint,
}: IgamingHobbiesProps): React.JSX.Element {
  const activeLocale: Locale = locale === 'es' ? 'es' : 'en'
  const t = localesDict[activeLocale]

  const submissionIdRef = useRef<string | null>(null)

  const [currentStep, setCurrentStep] = useState<number>(-1) // -1 = Hero / Manifesto, 0..9 = Survey Questions, 10 = Success
  const [answers, setAnswers] = useState<Record<number, SurveyAnswer>>({})
  const [otherInputs, setOtherInputs] = useState<Record<number, string>>({})
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)

  const handleStart = () => {
    setCurrentStep(0)
    setTimeout(() => {
      document.getElementById('survey-section')?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const handleAnswerSelect = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }

  const handleMultiAnswerSelect = (questionId: number, value: string, maxLimit?: number) => {
    const currentAnswer = answers[questionId]
    const current = Array.isArray(currentAnswer) ? currentAnswer : []
    let updated: string[]
    if (current.includes(value)) {
      updated = current.filter((item) => item !== value)
    } else {
      if (maxLimit && current.length >= maxLimit) {
        return // Limit met
      }
      updated = [...current, value]
    }
    setAnswers((prev) => ({
      ...prev,
      [questionId]: updated,
    }))
  }

  const handleTextAnswer = (questionId: number, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }

  const handleNext = async () => {
    setSearchQuery('') // Clear search filter on step transition
    if (currentStep < surveyQuestions.length - 1) {
      setCurrentStep((prev) => prev + 1)
    } else {
      await submitSurveyAnswers()
    }
    window.scrollTo({
      top: document.getElementById('survey-section')?.offsetTop || 0,
      behavior: 'smooth',
    })
  }

  const handlePrev = () => {
    setSearchQuery('') // Clear search filter on step transition
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
      window.scrollTo({
        top: document.getElementById('survey-section')?.offsetTop || 0,
        behavior: 'smooth',
      })
    } else if (currentStep === 0) {
      setCurrentStep(-1)
    }
  }

  const submitSurveyAnswers = async () => {
    setIsSubmitting(true)
    setIsError(false)

    if (!submissionIdRef.current) {
      submissionIdRef.current = crypto.randomUUID()
    }

    try {
      const payload = {
        submissionId: submissionIdRef.current,
        landingId,
        landingSlug,
        locale: activeLocale,
        submittedAt: new Date().toISOString(),
        answers: { ...answers },
      }

      const response = await fetch(surveyEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (response.ok) {
        setCurrentStep(surveyQuestions.length)
        triggerBlueConfetti()
      } else {
        setIsError(true)
      }
    } catch {
      setIsError(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  const triggerBlueConfetti = () => {
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js'
    script.onload = () => {
      const confettiFunc = (window as WindowWithConfetti).confetti
      if (confettiFunc) {
        const duration = 3 * 1000
        const end = Date.now() + duration

        const frame = () => {
          confettiFunc({
            particleCount: 5,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.8 },
            colors: ['#000FFF', '#FD1FE2', '#1E90FF', '#00BFFF'],
          })
          confettiFunc({
            particleCount: 5,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.8 },
            colors: ['#000FFF', '#FD1FE2', '#1E90FF', '#00BFFF'],
          })

          if (Date.now() < end) {
            requestAnimationFrame(frame)
          }
        }
        frame()
      }
    }
    document.body.appendChild(script)
  }

  const progressPercentage = Math.round((currentStep / surveyQuestions.length) * 100)
  const currentQuestion =
    currentStep >= 0 && currentStep < surveyQuestions.length
      ? surveyQuestions[currentStep]
      : undefined
  const currentOptions = currentQuestion?.options
  const otherInputId = `other-answer-${currentQuestion?.id}`

  return (
    <div className={styles.widgetContainer}>
      <header className={styles.header}>
        <div className={`${styles.headerContent} ${styles.maxContainer}`}>
          <Link href="https://belatragames.com/en" className={styles.brandLogo}>
            <span className={styles.brandName}>BELATRA</span>
            <span className={styles.brandCollaborator}>&nbsp;x G&M</span>
          </Link>
          <div className={styles.surveyTag}>
            <span>SURVEY 2026</span>
          </div>
        </div>
      </header>

      <section
        className={styles.hero}
        style={{
          backgroundImage: `url(${heroBackground.src})`,
        }}
      >
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <span className={styles.heroTag}>{t.collaboration}</span>
          <h1 className={styles.heroTitle}>
            <span className={styles.lineText}>Belatra Games</span>
            <span className={styles.xConnector}>x</span>
            <span className={styles.collabText}>G&M News</span>
          </h1>
          <p className={styles.heroSubtitle}>{t.mainHobbiesTitle}</p>
        </div>
      </section>

      {/* Manifesto Section */}
      <section className={styles.manifesto}>
        <div className={styles.maxManifestoContainer}>
          <div className={styles.textCenter}>
            <span className={styles.subTag}>{t.manifestoTag}</span>
            <h2 className={styles.sectionTitle}>{t.manifestoTitle}</h2>
            <div className={styles.divider}></div>
          </div>

          <div className={styles.manifestoCard}>
            <div className={styles.textWrapper}>
              <p>{t.manifestoP1}</p>
              <p>{t.manifestoP2}</p>
              <p>{t.manifestoP3}</p>
              <p className={styles.whyHobbyHeader}>{t.whyHobby}</p>
              <p>{t.hobbiesP1}</p>
              <p>{t.hobbiesP2}</p>
              <p className={styles.slogan}>{t.shiningSlogan}</p>
            </div>

            <div className={styles.ctaWrapper}>
              {currentStep === -1 && (
                <div className={styles.infoBox}>
                  <p className={styles.infoText}>{t.surveyMeta}</p>
                  <button type="button" onClick={handleStart} className={styles.glowButton}>
                    <span className={styles.buttonBg}></span>
                    <span className={styles.buttonFlare}></span>
                    <span className={styles.buttonLabel}>{t.beginSurvey}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Characters Showcase Section */}
      <section className={styles.charactersSection}>
        <div className={styles.maxContainer}>
          <h3 className={styles.charactersHeader}>{t.charactersSectionHeader}</h3>
          <div className={styles.sliderContainer}>
            <div className={styles.sliderTrack}>
              {/* Winter Fisherman */}
              <div className={styles.characterCard}>
                <div className={styles.imgWrapper}>
                  <Image src={characterFisherman} alt="Winter Fisherman" />
                </div>
                <h4 className={styles.charName}>{t.charFishermanName}</h4>
                <p className={styles.charGame}>{t.charFishermanTitle}</p>
                <p className={styles.charDesc}>{t.charFishermanDesc}</p>
              </div>

              {/* Summer Fisherman */}
              <div className={styles.characterCard}>
                <div className={styles.imgWrapper}>
                  <Image src={characterBow} alt="Summer Fisherman" />
                </div>
                <h4 className={styles.charName}>{t.charSummerName}</h4>
                <p className={styles.charGame}>{t.charSummerTitle}</p>
                <p className={styles.charDesc}>{t.charSummerDesc}</p>
              </div>

              {/* Hunter */}
              <div className={styles.characterCard}>
                <div className={styles.imgWrapper}>
                  <Image src={characterHunter} alt="Hunter" />
                </div>
                <h4 className={styles.charName}>{t.charHunterName}</h4>
                <p className={styles.charGame}>{t.charHunterTitle}</p>
                <p className={styles.charDesc}>{t.charHunterDesc}</p>
              </div>

              {/* Mystery Upcoming Character Card */}
              <div className={`${styles.characterCard} ${styles.secretCard}`}>
                <div className={styles.secretImgWrapper}>
                  <div className={styles.relativeSecretImgGradient}></div>
                  <div className={styles.relativeSecretImgWrapper}>
                    <div className={styles.secretImgRoundOut}></div>
                    <div className={styles.secretImgRoundIn}></div>
                    <span className={styles.secretImgRoundText}>?</span>
                  </div>
                </div>
                <h4 className={styles.secretName}>{t.charSecretName}</h4>
                <p className={styles.secretGame}>{t.charSecretTitle}</p>
                <p className={styles.secretDesc}>
                  {t.charSecretDesc}
                  <br />
                  {t.charSecretDescSecond}
                </p>
              </div>
            </div>

            <div className={styles.swipeIndicator}>
              <span>{t.swipeScrollHelp}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Survey Interactive Section */}
      {currentStep >= 0 && (
        <section id="survey-section" className={styles.surveySection}>
          <div className={styles.glowBg}></div>
          <div className={`${styles.maxSurveyContainer}`}>
            {currentQuestion ? (
              <div className={styles.surveyCard}>
                {/* Meta details & headers */}
                <div>
                  <div className={styles.metaInfo}>
                    <span>
                      {t.questionMeta
                        .replace('{current}', String(currentStep + 1))
                        .replace('{total}', String(surveyQuestions.length))}
                    </span>
                    <span>
                      {t.percentComplete.replace('{percent}', String(progressPercentage))}
                    </span>
                  </div>
                  <div className={styles.progressTrack}>
                    <div
                      className={styles.progressBar}
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>

                  <h3 className={styles.questionText}>{currentQuestion.question[activeLocale]}</h3>

                  {/* Search options bar for Q4 and Q6 */}
                  {(currentQuestion.id === 4 || currentQuestion.id === 6) && (
                    <div className={styles.searchWrapper}>
                      <div className={styles.searchIcon}>
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>
                      <input
                        id={`hobby-search-${currentQuestion.id}`}
                        name={`hobby-search-${currentQuestion.id}`}
                        type="text"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        className={styles.searchInput}
                        placeholder={t.searchPlaceholder}
                      />
                    </div>
                  )}

                  {/* Question Answers container */}
                  <div className={styles.optionsList}>
                    {currentOptions
                      ? currentOptions[activeLocale]
                          .filter((option) => {
                            if (currentQuestion.id !== 4 && currentQuestion.id !== 6) {
                              return true
                            }

                            return option.toLowerCase().includes(searchQuery.toLowerCase())
                          })
                          .map((option) => {
                            const optionIndex = currentOptions[activeLocale].indexOf(option)
                            const originalEnOption = currentOptions.en[optionIndex] ?? option
                            const currentAnswer = answers[currentQuestion.id]

                            const isSelected =
                              currentQuestion.type === 'single' ||
                              currentQuestion.type === 'single-with-other'
                                ? currentAnswer === originalEnOption
                                : Array.isArray(currentAnswer) &&
                                  currentAnswer.includes(originalEnOption)

                            return (
                              <button
                                key={originalEnOption}
                                type="button"
                                onClick={() => {
                                  if (
                                    currentQuestion.type === 'single' ||
                                    currentQuestion.type === 'single-with-other'
                                  ) {
                                    // Store English values as canonical backend values.
                                    handleAnswerSelect(currentQuestion.id, originalEnOption)
                                    return
                                  }

                                  const limit =
                                    currentQuestion.type === 'multi-with-other-limit-3'
                                      ? 3
                                      : undefined

                                  handleMultiAnswerSelect(
                                    currentQuestion.id,
                                    originalEnOption,
                                    limit,
                                  )
                                }}
                                className={`${styles.optionButton} ${
                                  isSelected ? styles.isSelected : ''
                                }`}
                              >
                                <span className={styles.optionLabel}>{option}</span>
                                <div className={styles.indicatorCircle}>
                                  {isSelected && <div className={styles.indicatorDot}></div>}
                                </div>
                              </button>
                            )
                          })
                      : null}

                    {/* custom write-in field rendering */}
                    {currentQuestion.type.includes('other') && (
                      <div className={styles.otherField}>
                        <label htmlFor={otherInputId} className={styles.otherLabel}>
                          {t.otherLabel}
                        </label>
                        <input
                          id={otherInputId}
                          name={otherInputId}
                          type="text"
                          value={otherInputs[currentQuestion.id] ?? ''}
                          onChange={(event) => {
                            const value = event.target.value

                            setOtherInputs((prev) => ({
                              ...prev,
                              [currentQuestion.id]: value,
                            }))

                            if (currentQuestion.type.startsWith('single')) {
                              handleAnswerSelect(currentQuestion.id, `Other: ${value}`)
                              return
                            }

                            const currentAnswer = answers[currentQuestion.id]
                            const currentValues = Array.isArray(currentAnswer) ? currentAnswer : []
                            const filteredValues = currentValues.filter(
                              (item) => !item.startsWith('Other:'),
                            )

                            setAnswers((prev) => ({
                              ...prev,
                              [currentQuestion.id]: value.trim()
                                ? [...filteredValues, `Other: ${value}`]
                                : filteredValues,
                            }))
                          }}
                          className={styles.otherInput}
                          placeholder={t.otherPlaceholder}
                        />
                      </div>
                    )}

                    {/* text-area inputs */}
                    {currentQuestion.type === 'text' && (
                      <textarea
                        value={
                          typeof answers[currentQuestion.id] === 'string'
                            ? answers[currentQuestion.id]
                            : ''
                        }
                        onChange={(event) =>
                          handleTextAnswer(currentQuestion.id, event.target.value)
                        }
                        className={styles.textareaField}
                        placeholder={currentQuestion.placeholder?.[activeLocale] ?? ''}
                      />
                    )}
                  </div>
                </div>

                {currentOptions && currentOptions[activeLocale].length > 5 && (
                  <div className={styles.scrollHelper}>
                    <span>{t.scrollDownHelp}</span>
                  </div>
                )}

                {/* Navigation actions layout container */}
                <div className={styles.navigationControls}>
                  <button type="button" onClick={handlePrev} className={styles.backButton}>
                    {t.backBtn}
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={isSubmitting}
                    className={styles.nextButton}
                  >
                    {isSubmitting
                      ? '...'
                      : currentStep === surveyQuestions.length - 1
                        ? t.submitBtn
                        : t.nextBtn}
                  </button>
                </div>
                {isError && (
                  <p
                    style={{
                      color: '#FD1FE2',
                      fontSize: '0.75rem',
                      marginTop: '1rem',
                      textAlign: 'center',
                      fontFamily: 'monospace',
                    }}
                  >
                    An error occurred while submitting. Please try again.
                  </p>
                )}
              </div>
            ) : (
              /* Success Landing page view */
              <div className={styles.successCard}>
                <div className={styles.checkIcon}>✓</div>
                <h3 className={styles.successTitle}>{t.successAlertTitle}</h3>
                <p className={styles.successText}>{t.successAlertDesc}</p>

                <div className={styles.publishingInfoBox || styles.publishingBox}>
                  <div className={styles.glowingBar}></div>
                  <p className={styles.publishingText}>
                    {t.publishingTextBefore} <strong className={styles.highlightG}>G&M News</strong>{' '}
                    {t.publishingTextMiddle}{' '}
                    <strong className={styles.highlightB}>Belatra Games</strong>{' '}
                    {t.publishingTextAfter}
                  </p>
                  <p className={styles.stayTunedTag}>{t.stayTuned}</p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setAnswers({})
                    setOtherInputs({})
                    setCurrentStep(-1)
                    submissionIdRef.current = null
                  }}
                  className={styles.resetButton}
                >
                  {t.resetBtn}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer view layout markup */}
      <footer className={styles.footer}>
        <div className={`${styles.footerContent} ${styles.maxContainer}`}>
          <div className={styles.footerLogo}>
            <span>BELATRA GAMES x G&M NEWS</span>
          </div>
          <p className={styles.copyright}>&copy; 2026 Belatra Games & G&M News. {t.copyright}</p>
        </div>
      </footer>
    </div>
  )
}
export default IgamingHobbies
